import React from "react";
import {
  ArrowLeft, ArrowRight, User, Calendar, Clock, Briefcase,
  MapPin, Timer, AlertCircle,
} from "lucide-react";
import ApprovalTimeline from "./ApprovalTimeline";
import ApprovalActions from "./ApprovalActions";

/**
 * OvertimeRequestDetail
 * Full detail view of a single overtime request inside the drawer.
 */
const OvertimeRequestDetail = ({
  record,
  theme,
  isRTL,
  t,
  language,
  onBack,
  onApprove,
  onReject,
  canApprove = true,
  onActionComplete,
  getConfigForEmployee,
}) => {
  const isDark = theme === "dark";

  const overtimeMinutes = parseInt(record.overTime, 10) || 0;
  const formatOT = (mins) => {
    if (mins >= 60) {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return m > 0 ? `${h}${t.hour} ${m}${t.minutes}` : `${h}${t.hour}`;
    }
    return `${mins}${t.minutes}`;
  };

  // Get config for this employee to show shift info
  const config = getConfigForEmployee?.(
    record.workingCompany,
    record.employeeType,
    record.date
  ) || {};

  // Avatar
  const name = record.employeeName || "";
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

  const avatarColors = [
    ["#6366f1", "#4f46e5"], ["#ec4899", "#db2777"],
    ["#f59e0b", "#d97706"], ["#10b981", "#059669"],
    ["#3b82f6", "#2563eb"], ["#8b5cf6", "#7c3aed"],
  ];
  const colorIdx = name.length % avatarColors.length;
  const [avatarC1, avatarC2] = avatarColors[colorIdx];

  const BackIcon = isRTL ? ArrowRight : ArrowLeft;

  // ── Section component ──────────────────────────────────────────────────
  const Section = ({ icon: Icon, title, children, iconColor }) => (
    <div style={{ marginBottom: "20px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "12px",
          flexDirection: isRTL ? "row-reverse" : "row",
        }}
      >
        <div
          style={{
            width: "28px",
            height: "28px",
            borderRadius: "8px",
            background: isDark ? `${iconColor}20` : `${iconColor}12`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={14} color={iconColor} strokeWidth={2.5} />
        </div>
        <h4
          style={{
            margin: 0,
            fontSize: "14px",
            fontWeight: 700,
            color: isDark ? "#f1f5f9" : "#1e293b",
            fontFamily: "'Cairo', sans-serif",
          }}
        >
          {title}
        </h4>
      </div>
      <div
        style={{
          padding: "14px 16px",
          borderRadius: "12px",
          background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.015)",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}`,
        }}
      >
        {children}
      </div>
    </div>
  );

  // ── InfoRow ────────────────────────────────────────────────────────────
  const InfoRow = ({ label, value, valueColor }) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "6px 0",
        flexDirection: isRTL ? "row-reverse" : "row",
      }}
    >
      <span
        style={{
          fontSize: "13px",
          fontWeight: 500,
          color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
          fontFamily: "'Cairo', sans-serif",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: "13px",
          fontWeight: 600,
          color: valueColor || (isDark ? "#f1f5f9" : "#1e293b"),
          fontFamily: "'Cairo', sans-serif",
        }}
      >
        {value || "-"}
      </span>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* ── Back button ─────────────────────────────────────────────────── */}
      <button
        onClick={onBack}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "8px 14px",
          borderRadius: "10px",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
          background: "transparent",
          color: isDark ? "#94a3b8" : "#64748b",
          fontSize: "13px",
          fontWeight: 500,
          cursor: "pointer",
          fontFamily: "'Cairo', sans-serif",
          marginBottom: "20px",
          alignSelf: isRTL ? "flex-end" : "flex-start",
          flexDirection: isRTL ? "row-reverse" : "row",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
        }}
      >
        <BackIcon size={14} />
        {t.backToList}
      </button>

      {/* ── Employee Header ─────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginBottom: "24px",
          padding: "16px",
          borderRadius: "14px",
          background: isDark
            ? "linear-gradient(135deg, rgba(99,102,241,0.08), rgba(99,102,241,0.03))"
            : "linear-gradient(135deg, rgba(99,102,241,0.06), rgba(99,102,241,0.02))",
          border: `1px solid ${isDark ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.1)"}`,
          flexDirection: isRTL ? "row-reverse" : "row",
        }}
      >
        <div
          style={{
            width: "52px",
            height: "52px",
            borderRadius: "14px",
            background: `linear-gradient(135deg, ${avatarC1}, ${avatarC2})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: "18px",
            fontWeight: 700,
            fontFamily: "'Cairo', sans-serif",
            flexShrink: 0,
            boxShadow: `0 6px 16px ${avatarC1}40`,
          }}
        >
          {initials}
        </div>
        <div style={{ flex: 1, textAlign: isRTL ? "right" : "left" }}>
          <div
            style={{
              fontSize: "17px",
              fontWeight: 700,
              color: isDark ? "#f1f5f9" : "#1e293b",
              fontFamily: "'Cairo', sans-serif",
              marginBottom: "2px",
            }}
          >
            {record.employeeName || record.employeeId}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)",
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            ID: {record.employeeId}
            {record.clientName ? ` · ${record.clientName}` : ""}
          </div>
        </div>
        {/* OT Badge */}
        <div
          style={{
            padding: "8px 14px",
            borderRadius: "12px",
            background: isDark ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.1)",
            color: isDark ? "#a5b4fc" : "#4f46e5",
            fontSize: "16px",
            fontWeight: 800,
            fontFamily: "'Cairo', sans-serif",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            flexShrink: 0,
          }}
        >
          <Clock size={16} />
          {formatOT(overtimeMinutes)}
        </div>
      </div>

      {/* ── Scrollable content ──────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: "auto", paddingRight: isRTL ? 0 : "4px", paddingLeft: isRTL ? "4px" : 0 }}>
        {/* Employee Info */}
        <Section icon={User} title={t.employeeInfo} iconColor="#6366f1">
          <InfoRow label={t.employeeName} value={record.employeeName} />
          <InfoRow label={t.employeeID} value={record.employeeId} />
          <InfoRow label={t.client} value={record.clientName} />
        </Section>

        {/* Attendance Info */}
        <Section icon={Calendar} title={t.attendanceInfo} iconColor="#3b82f6">
          <InfoRow
            label={t.date}
            value={
              record.date
                ? new Date(record.date).toLocaleDateString(
                    isRTL ? "ar-EG" : "en-US",
                    { year: "numeric", month: "long", day: "numeric", weekday: "long" }
                  )
                : "-"
            }
          />
          <InfoRow
            label={t.scheduledShift}
            value={
              config.workStartTime && config.workEndTime
                ? `${config.workStartTime} — ${config.workEndTime}`
                : "-"
            }
          />
          <InfoRow label={t.actualCheckIn} value={record.checkIn} />
          <InfoRow label={t.actualCheckOut} value={record.checkOut} />
          <InfoRow
            label={t.totalWorkedHours}
            value={
              record.hoursWorked && record.hoursWorked !== "-"
                ? `${Math.round(parseFloat(record.hoursWorked) * 10) / 10} ${t.hours}`
                : "-"
            }
          />
          <InfoRow label={t.status} value={record.status} />
        </Section>

        {/* Overtime Info */}
        <Section icon={Timer} title={t.overtimeInfo} iconColor="#f59e0b">
          <InfoRow
            label={t.shiftEndTime}
            value={config.workEndTime || "-"}
          />
          <InfoRow
            label={t.actualEndTime}
            value={record.checkOut}
          />
          <InfoRow
            label={t.overtimeDuration}
            value={formatOT(overtimeMinutes)}
            valueColor="#6366f1"
          />
          {record.underTime && record.underTime !== "-" && (
            <InfoRow
              label={t.tardiness}
              value={record.underTime}
              valueColor="#ef4444"
            />
          )}
        </Section>

        {/* Approval Timeline */}
        <Section icon={AlertCircle} title={t.approvalTimeline} iconColor="#8b5cf6">
          <ApprovalTimeline record={record} theme={theme} isRTL={isRTL} t={t} />
        </Section>
      </div>

      {/* ── Action buttons — hidden without the approve_overtime feature ── */}
      {canApprove && (
        <ApprovalActions
          record={record}
          theme={theme}
          isRTL={isRTL}
          t={t}
          onApprove={onApprove}
          onReject={onReject}
          onActionComplete={onActionComplete}
        />
      )}
    </div>
  );
};

export default OvertimeRequestDetail;
